/**
 * Validate Runtime Architecture Executor (workspace)
 *
 * Two workspace-level checks on the runtime microservice graph:
 *   1. No-cycles: every runtime cycle must be in the `allowedCycles` allowlist
 *      with an unexpired `until`; any other cycle fails the build.
 *   2. Unchanged: the freshly-assembled graph must match the committed
 *      architecture/runtime-dependencies.json (run `architecture:generate`).
 *
 * On/off + a whole-rule grace window come from webpieces.config.json
 * (rule: runtime-architecture).
 *
 * Usage: nx run architecture:validate-runtime-architecture
 */

import type { ExecutorContext } from '@nx/devkit';
import { loadBlessedGraph } from '../../lib/graph-loader';
import type { EnhancedGraph } from '../../lib/graph-sorter';
import {
    deriveRuntimeGraphReport,
    loadRuntimeGraph,
    runtimeAdjacency,
    runtimeGraphFileExists,
    serializeRuntimeGraph,
} from '../../lib/runtime-graph';
import type { RuntimeGraph } from '../../lib/runtime-graph';
import { findRuntimeCycles, cycleKey } from '../../lib/runtime-cycles';
import { printAutoHiddenServers } from '../../lib/runtime-participant-resolver';
import type { AllowedCycle } from '../../lib/runtime-config';
import { loadRuntimeConfig, runtimeReportOnly, RUNTIME_RULE_NAME } from '../../lib/runtime-config';

export interface ValidateRuntimeArchitectureOptions {
    // Config comes from webpieces.config.json at runtime.
}

export interface ExecutorResult {
    success: boolean;
}

/** Allowlist keys (sorted-services -> entry) that are still in their grace window. */
function activeAllowedKeys(allowed: AllowedCycle[]): Map<string, AllowedCycle> {
    const nowSeconds = Date.now() / 1000;
    const map = new Map<string, AllowedCycle>();
    for (const entry of allowed) {
        const active = entry.until === undefined || nowSeconds < entry.until;
        if (active) map.set(cycleKey(entry.services), entry);
    }
    return map;
}

/** Returns disallowed-cycle messages (empty = all cycles allowed or none). */
function checkCycles(graph: RuntimeGraph, allowed: AllowedCycle[]): string[] {
    const cycles = findRuntimeCycles(runtimeAdjacency(graph));
    if (cycles.length === 0) return [];

    const activeAllow = activeAllowedKeys(allowed);
    const problems: string[] = [];
    for (const cycle of cycles) {
        const entry = activeAllow.get(cycle.key);
        if (entry) {
            console.log(`⏳ Allowed runtime cycle [${cycle.services.join(' <-> ')}] — ${entry.reason ?? 'no reason given'}`);
            continue;
        }
        problems.push(`runtime cycle: ${cycle.services.join(' -> ')} -> ${cycle.services[0]}`);
    }
    return problems;
}

/**
 * Every `role:server` project must have a NODE in the runtime graph. A pure set difference against
 * what the derivation produced, and it exists because the derivation once dropped relation-less
 * servers silently: the diagram claimed to be complete while a deployed service was missing from it.
 * Now the only sanctioned way for a server to be absent from the drawing is `drawOnGraph:false`, and
 * anything else that ever removes one fails the build by name instead of disappearing quietly.
 */
// webpieces-disable no-function-outside-class -- executor step helper, matches checkCycles/checkUnchanged in this file
export function checkServersPresent(
    projects: EnhancedGraph,
    hiddenProjects: Set<string>,
    graph: RuntimeGraph,
): string[] {
    const problems: string[] = [];
    for (const name of Object.keys(projects).sort()) {
        if (projects[name].role !== 'server') continue;
        if (hiddenProjects.has(name)) continue;
        if (graph.services[name] !== undefined) continue;
        problems.push(
            `role:server '${name}' has no node in the runtime graph. Tag it drawOnGraph:false in its ` +
                `project.json if that is intended.`,
        );
    }
    return problems;
}

/** Returns an unchanged-check message, or null if the committed graph matches. */
function checkUnchanged(workspaceRoot: string, current: RuntimeGraph): string | null {
    if (!runtimeGraphFileExists(workspaceRoot)) {
        return 'No committed architecture/runtime-dependencies.json — run: nx run architecture:generate';
    }
    const saved = loadRuntimeGraph(workspaceRoot);
    if (saved && serializeRuntimeGraph(saved) === serializeRuntimeGraph(current)) return null;
    return 'Runtime graph changed since last commit — run: nx run architecture:generate and commit the result';
}

export default async function runExecutor(
    _options: ValidateRuntimeArchitectureOptions,
    context: ExecutorContext,
): Promise<ExecutorResult> {
    const workspaceRoot = context.root;
    const config = loadRuntimeConfig(workspaceRoot);

    if (config.off) {
        console.log(`\n⏭️  Skipping ${RUNTIME_RULE_NAME} (mode: OFF)\n`);
        return { success: true };
    }

    console.log('\n📡 Validating runtime microservice architecture\n');
    // Derive the "current" runtime graph from the committed dependencies.json — the SAME source
    // architecture:generate derives from — so the unchanged-check compares like-for-like and can
    // never fail on a clean, freshly-generated tree. (validate-architecture-unchanged separately
    // guarantees dependencies.json itself is fresh.)
    const depsFile = loadBlessedGraph(workspaceRoot);
    if (depsFile === null) {
        console.error('❌ No architecture/dependencies.json — run: nx run architecture:generate');
        return { success: false };
    }
    const hiddenProjects = new Set<string>();
    for (const name of Object.keys(depsFile.projects)) {
        if (depsFile.projects[name].drawOnGraph === false) hiddenProjects.add(name);
    }
    // apiContracts comes from the SAME loaded file, so the queue/trigger data the derivation sees
    // here is byte-identical to what generate saw in memory.
    const report = deriveRuntimeGraphReport(
        depsFile.projects,
        hiddenProjects,
        depsFile.apiContracts,
        depsFile.externalSystems,
    );
    const graph = report.graph;
    for (const warning of report.warnings) console.warn(`⚠️  ${warning}`);
    printAutoHiddenServers(report.autoHidden);

    // A call site naming a service no module answers to FAILS: the contract is served in-repo, so
    // the name is a typo or a stale rename, and the graph only ever hid it by fanning the edge out.
    const problems: string[] = [
        ...report.problems,
        ...checkCycles(graph, config.allowedCycles),
        ...checkServersPresent(depsFile.projects, hiddenProjects, graph),
    ];
    const unchanged = checkUnchanged(workspaceRoot, graph);
    if (unchanged) problems.push(unchanged);

    for (const u of graph.unresolvedUses) {
        console.log(`⚠️  ${u.service} uses "${u.api}" but no in-repo service implements it (external?)`);
    }

    if (problems.length === 0) {
        console.log('✅ Runtime architecture valid (no disallowed cycles, graph unchanged)\n');
        return { success: true };
    }

    console.error('\n❌ Runtime architecture validation failed:\n');
    for (const p of problems) console.error(`  - ${p}`);
    console.error('\nAllow a cycle temporarily via runtime-architecture.allowedCycles (services + reason + until) in webpieces.config.json.\n');

    const reportOnly = runtimeReportOnly(config);
    if (reportOnly.skip) {
        console.log(`⏳ Reported but not failing (${reportOnly.reason}).\n`);
        return { success: true };
    }
    return { success: false };
}
